import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";
import { useSnackbar } from "notistack";
import {
  Button,
  Loader,
  RuleGroup,
  Table,
  TableInstance,
} from "@monolith-forensics/monolith-ui";

import AcquisitionsAPI from "@/api/acquisitions/index.js";
import { getDateFormat } from "@/utils/date-format";
import { Case } from "@/pages/Cases/types/Cases";

import { Acquisition } from "../acquisitions.types";
import {
  AcquisitionsQuery,
  GetAcquisitionsQuery,
  resolveAcquisitionsQueryBody,
} from "../acquisitions.query";
import {
  useAcquisitionColumnDefinitions,
  useAcquisitionFilterDefinitions,
} from "../Hooks";
import { AcquisitionFlyout } from "../Flyout";
import { isCompleteRule } from "../utils/ruleGroupToConditions";
import { useTableState } from "@/Features/_shared";

const DEFAULT_PAGE_SIZE = 100;

export type StoredDataTableProps = {
  storageInfo: {
    storage_id: number;
    case_id?: number;
  };
  stateStoreKey?: string;
};

/**
 * Strips empty/incomplete rules from the mfui filter RuleGroup before it drives
 * a fetch — mirrors EvidenceTable so half-entered filters don't query.
 */
const resolveFilterState = (filterState: RuleGroup | undefined) => {
  if (!filterState) {
    return undefined;
  }
  if (filterState.rules.length > 0) {
    // Ignore any empty rule sets. isCompleteRule is the same predicate the
    // conditions mapping applies, so the client-side sanitize can't drift
    // from what actually gets sent.
    const filteredRules = filterState.rules.filter(isCompleteRule);
    if (filteredRules.length > 0) {
      return {
        ...filterState,
        rules: filteredRules,
      };
    }
  }
  return undefined;
};

export const StoredDataTable: React.FC<StoredDataTableProps> = ({
  storageInfo,
  stateStoreKey = "storage:stored-data:table",
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();

  const tableInstanceRef = useRef<TableInstance>();
  const [focusedRowData, setFocusedRowData] = useState<Acquisition | null>(
    null
  );
  const [showFlyout, setShowFlyout] = useState(false);

  const {
    columnDefinitions,
    isSuccess: isColumnDefinitionsReady,
    isError: isColumnDefinitionsError,
    refetch: refetchColumnDefinitions,
  } = useAcquisitionColumnDefinitions();

  const filters = useAcquisitionFilterDefinitions();

  const tableState = useTableState<GetAcquisitionsQuery>(
    { storage_id: storageInfo.storage_id },
    stateStoreKey,
    DEFAULT_PAGE_SIZE
  );

  // useTableState restores persisted filter state raw (mfui saves rules
  // mid-edit, incl. empty/partial values), so re-apply resolveFilterState here —
  // otherwise a half-entered saved rule drives the first fetch with
  // undefined/empty bind values. The live path is already sanitized in
  // onFilterChange; this is idempotent for it.
  const resolvedQuery = useMemo<GetAcquisitionsQuery>(
    () => ({
      ...tableState.resolvedQuery,
      // Deterministic page boundaries: without an ORDER BY every
      // LIMIT/OFFSET page is separately unordered, so rows can duplicate or
      // vanish across infinite-scroll pages. created_on is in the server's
      // fieldMap (acq_id is not) and the server adds an acq_name tiebreaker.
      ...(tableState.resolvedQuery.sort_by
        ? {}
        : { sort_by: "created_on", sort_dir: "desc" as const }),
      filter: resolveFilterState(tableState.resolvedQuery.filter),
    }),
    [tableState.resolvedQuery]
  );

  const {
    data,
    isLoading,
    isFetched,
    isFetchingNextPage,
    hasNextPage,
    isError: isAcquisitionsError,
    refetch,
    fetchNextPage,
  } = AcquisitionsQuery.useGetInfiniteAcquisitions(resolvedQuery, filters, {
    // Wait for the custom-field definitions: a persisted custom-field date
    // filter resolved before they load would be typed "text" (wrong SQL path)
    // and stay cached, since the definitions are not part of the queryKey.
    // isSuccess (not isFetched) so an errored definitions fetch fails closed.
    enabled: isColumnDefinitionsReady,
  });

  // The queryFn validates page shape and throws on the API layer's resolved
  // failures, so pages here are well-formed; the optional chaining is just
  // defensive.
  const records = useMemo(
    () => data?.pages?.flatMap((page) => page?.data ?? []) ?? [],
    [data?.pages]
  );

  const totalRecords = data?.pages?.[0]?.total || 0;

  // Guard against repeated scroll-to-bottom events: an unconditional
  // fetchNextPage() cancels and restarts the in-flight page request
  // (cancelRefetch defaults to true), so continued scrolling can starve the
  // next page indefinitely.
  const debouncedFetchNextPage = useDebouncedCallback(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, 50);

  const debouncedSearch = useDebouncedCallback((value) => {
    tableState.setSearchState(value);
  }, 300);

  const handleExportTable = useCallback(
    (option: string) => {
      enqueueSnackbar("Exporting acquisition table...", {
        variant: "info",
      });

      let columns = (tableInstanceRef.current?.columnState ?? [])
        .filter((c) => c.dataField !== "Action")
        .map((c) => ({
          dataField: c.dataField,
          header: c.caption || c.dataField,
          caption: c.caption || c.dataField,
          visible: c.visible,
          dataType: c.dataType,
        }));

      if (option === "export:visible") {
        columns = columns.filter((c) => c.visible !== false);
      }

      AcquisitionsAPI.exportAcquisitionList({
        query: resolveAcquisitionsQueryBody(resolvedQuery, filters),
        type: "xlsx",
        columns,
        date_format: getDateFormat({ isMoment: true, includeTime: true }),
        custom_date_format: getDateFormat({
          isMoment: true,
          includeTime: false,
        }),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      })
        .then((res: { signedUrl: string; filename: string }) => {
          const { signedUrl, filename } = res;
          const el = document.createElement("a");
          el.href = signedUrl.replace(
            "http://localhost:3000",
            "http://localhost:3001"
          );
          el.download = filename;
          el.click();
          el.remove();
        })
        .catch(() => {
          enqueueSnackbar(
            "Failed to export acquisition list. Please try again.",
            { variant: "error" }
          );
        });
    },
    [resolvedQuery, filters, enqueueSnackbar]
  );

  const handleActionButtonClick = (rowData: Acquisition) => {
    setFocusedRowData(rowData);
    setShowFlyout(true);
  };
  const handleAcquisitionEdited = () => {
    refetch();
  };
  const handleAcquisitionDeleted = () => {
    setShowFlyout(false);
    setFocusedRowData(null);
    refetch();
  };
  const handleCopyFinished = () => {
    refetch();
  };
  // When the focused row is no longer in the list (deleted, or filtered out
  // by a refetch) findIndex is -1; fall through so Next lands on the first
  // record and Prev on the last instead of the buttons going dead.
  const handleGetNextItem = (acq_id: number) => {
    if (records.length === 0) return;
    const current = records.findIndex((i) => i.acq_id === acq_id);
    setFocusedRowData(records[current + 1] || records[0]);
  };
  const handleGetPrevItem = (acq_id: number) => {
    if (records.length === 0) return;
    const current = records.findIndex((i) => i.acq_id === acq_id);
    setFocusedRowData(records[current - 1] || records[records.length - 1]);
  };

  // Read through a ref so clearAcqIdParam stays referentially stable across
  // unrelated search-param changes — it's a dep of the deep-link effect
  // below, and an unstable identity would re-run the fetch and snap the
  // flyout back to the deep-linked record mid Next/Prev navigation.
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  // Drop the deep-link param once the flyout closes (or the link is bad) so
  // returning to this view doesn't pop the same flyout back open.
  const clearAcqIdParam = useCallback(() => {
    const current = searchParamsRef.current;
    if (!current.has("acq_id")) return;
    const next = new URLSearchParams(current);
    next.delete("acq_id");
    setSearchParams(next, { replace: true });
  }, [setSearchParams]);

  // Deep link: opening ?acq_id=<id> shows that acquisition's flyout. Resolve
  // the full record before seeding — a partial { acq_id } seed hands the
  // flyout's Copy action a record with no case_id, so the copy would land in
  // the storage item's case (or none) instead of the acquisition's own.
  // Keyed on the param's string value (not the searchParams object) so only
  // an actual acq_id change re-runs the fetch.
  const acqIdParam = searchParams.get("acq_id");
  useEffect(() => {
    if (!acqIdParam) return;
    const parsedAcqID = parseInt(acqIdParam, 10);
    if (Number.isNaN(parsedAcqID)) {
      // A malformed deep link (?acq_id=abc) can never resolve — drop it so
      // it doesn't linger in the URL.
      clearAcqIdParam();
      return;
    }

    let cancelled = false;
    AcquisitionsAPI.getAcquisitions({ acq_id: parsedAcqID })
      .then((res: Acquisition[] | undefined) => {
        if (cancelled) return;
        // The API layer resolves failures instead of rejecting, so a
        // non-array here is a failed request, not a missing record — keep
        // the param so a reload can retry, and only clear it when the
        // record is genuinely gone.
        if (!Array.isArray(res)) {
          enqueueSnackbar("Failed to load acquisition.", { variant: "error" });
          return;
        }
        const record = res[0];
        if (record) {
          setFocusedRowData(record);
          setShowFlyout(true);
        } else {
          enqueueSnackbar("Acquisition not found.", { variant: "error" });
          clearAcqIdParam();
        }
      })
      .catch(() => {
        if (cancelled) return;
        enqueueSnackbar("Failed to load acquisition.", { variant: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [acqIdParam, enqueueSnackbar, clearAcqIdParam]);

  // A failed custom-field definitions fetch would otherwise leave the fetch
  // gate (enabled: isColumnDefinitionsReady) permanently closed and strand the
  // tab on a spinner. Offer an explicit retry instead.
  if (isColumnDefinitionsError) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          padding: 10,
        }}
      >
        <div style={{ color: "red" }}>
          Failed to load acquisition columns. Please try again.
        </div>
        <Button
          size="xs"
          variant="subtle"
          onClick={() => refetchColumnDefinitions()}
        >
          Retry
        </Button>
      </div>
    );
  }

  if (!isColumnDefinitionsReady) {
    return <Loader />;
  }

  // Same failure shape as the definitions fetch: an errored list query never
  // sets isFetched, which would otherwise strand the tab on the loading
  // skeleton with no affordance beyond the global error toast.
  if (isAcquisitionsError) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          padding: 10,
        }}
      >
        <div style={{ color: "red" }}>
          Failed to load stored data. Please try again.
        </div>
        <Button size="xs" variant="subtle" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <>
      {showFlyout && focusedRowData && (
        <AcquisitionFlyout
          show={showFlyout}
          onClose={() => {
            setShowFlyout(false);
            setFocusedRowData(null);
            clearAcqIdParam();
          }}
          defaultInfo={focusedRowData}
          onEdit={handleAcquisitionEdited}
          onDelete={handleAcquisitionDeleted}
          onNext={() => handleGetNextItem(focusedRowData.acq_id)}
          onPrevious={() => handleGetPrevItem(focusedRowData.acq_id)}
          onCopy={handleCopyFinished}
          caseInfo={
            {
              case_id: focusedRowData.case_id ?? storageInfo.case_id,
            } as Case
          }
        />
      )}
      <Table
        data={records}
        columnProps={columnDefinitions}
        keyField="acq_id"
        virtualized={true}
        tableInstanceRef={tableInstanceRef}
        totalRecords={totalRecords}
        loading={isLoading || !isFetched || isFetchingNextPage}
        focusedRowId={focusedRowData?.acq_id}
        enableActionButton={true}
        stateStorage={{
          enabled: !!stateStoreKey,
          key: stateStoreKey,
          type: "localStorage",
        }}
        onActionButtonClick={handleActionButtonClick}
        onScrollToBottom={debouncedFetchNextPage}
        onColumnHeaderClick={({ sort }) =>
          tableState.setSortState(sort || undefined)
        }
        enableSorting={true}
        enableColumnReorder={true}
        enableColumnResize={true}
        manualSearch={true}
        manualSorting={true}
        manualFiltering={true}
        manualExport={true}
        tableMenuOptions={{
          enabled: true,
          tableCountOptions: {
            recordsTotalEnabled: true,
          },
          columnSelectorOptions: {
            enabled: true,
          },
          searchOptions: {
            enabled: true,
            onSearch: debouncedSearch,
            placeholder: "Search Acquisitions",
          },
          exportOptions: {
            enabled: true,
            onExport: handleExportTable,
          },
          filterOptions: {
            enabled: true,
            // The AND/OR toggle would promise OR, but ruleGroupToConditions
            // drops the combinator and the server's queryConditionResolver
            // only ever ANDs conditions.
            showCombinator: false,
            filterDefinitions: filters,
            onFilterChange: (query) => {
              tableState.setFilterState(resolveFilterState(query));
            },
          },
        }}
      />
    </>
  );
};
